import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  MessageSquare,
  Calendar,
  BookOpen,
  LineChart,
  PlayCircle,
  Sparkles,
  ArrowRight,
  ShieldAlert,
  UserPlus,
  ClipboardList,
  Zap,
  Check,
} from 'lucide-react';
import { CALENDLY_URL } from '../lib/siteConfig';

/**
 * Conversion-focused Apex AI Coach section for the /athletes page.
 *
 * Designed to reduce friction around the free-trial signup. Structure:
 *   1. Eyebrow + headline + positioning paragraph
 *   2. Trial trust strip  (7-day free trial · no card · cancel anytime)
 *   3. "How it Works" — 3 numbered steps so the signup path is unmistakable
 *   4. Feature highlights — 5 tiles pulled from the live app (Chat, Pre-Game,
 *      Journal, Dashboard, Video)
 *   5. Access clarification — surfaces both inclusion paths verbatim from the
 *      current pricing copy (multi-session package OR standalone subscription)
 *   6. CTAs — primary "Start Free Trial" + secondary "Book a Discovery Call"
 *   7. Boundaries note — legal scope retained from the original section
 *
 * NOTE: Every claim in this section is cross-checked against the live site:
 *   - "Start Free — 7-Day Trial, No Card" matches AICoachSection.js literal
 *   - "3- and 6-session packages include Apex AI Coach" matches
 *     SimplifiedPricing.js literal
 *   - Standalone trial language matches /app/pricing FEATURES.pro/premium
 */

const steps = [
  {
    n: '01',
    Icon: UserPlus,
    title: 'Create your account',
    blurb: 'Email + password. No credit card needed for the 7-day trial.',
  },
  {
    n: '02',
    Icon: ClipboardList,
    title: 'Set up your athlete profile',
    blurb: 'Sport, position, season goals. Takes about 2 minutes — the AI Coach uses this to tailor every response.',
  },
  {
    n: '03',
    Icon: Zap,
    title: 'Start using the AI Coach tools',
    blurb: 'Chat, build a pre-game plan, journal a tough night, check your progress. Pull it up before practice and watch it click.',
  },
];

const features = [
  { Icon: MessageSquare, name: 'AI Coach Chat',     blurb: "Voice-of-Coach-Lewis chat for in-the-moment performance questions." },
  { Icon: Calendar,      name: 'Pre-Game Prep',     blurb: 'Generate a structured plan: warm-up, cues, visualization, mantra.' },
  { Icon: BookOpen,      name: 'Journal & Reflect', blurb: 'Capture how a session went — get a short reflection + next-step prompts.' },
  { Icon: LineChart,     name: 'Progress Dashboard',blurb: 'Streaks, mood trend, themes, weekly summary across your training.' },
  { Icon: PlayCircle,    name: 'Video Tools',       blurb: 'Sport-specific drills and Apex Six framework walk-throughs.' },
];

const fadeIn = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
};

export const AICoachAthleteSection = () => {
  return (
    <section
      id="ai-coach"
      className="relative py-20 sm:py-24 bg-white"
      data-testid="ai-coach-athlete-section"
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">

        {/* 1. Header */}
        <motion.div {...fadeIn} className="max-w-[60ch] mx-auto text-center mb-8">
          <div
            className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#004C54] font-bold mb-3"
            data-testid="ai-coach-athlete-eyebrow"
          >
            Apex AI Coach
          </div>
          <h2
            className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-[#0a0d0e] mb-4 leading-[1.1]"
            data-testid="ai-coach-athlete-headline"
          >
            Daily mental performance support &mdash; between sessions.
          </h2>
          <p
            className="text-[16px] sm:text-[17px] text-[#3a3a3a] leading-[1.55]"
            data-testid="ai-coach-athlete-intro"
          >
            Apex AI Coach extends Coach Lewis&rsquo;s method into your phone. A private training partner you can pull up before a game, in the locker room, or after a tough night &mdash; built so athletes can get pre-game support, reset after mistakes, track progress, journal, and install the routines that hold under pressure.
          </p>
        </motion.div>

        {/* Trial trust strip */}
        <motion.div
          {...fadeIn}
          transition={{ delay: 0.05 }}
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-14 text-[13px] font-medium text-[#3a3a3a]"
          data-testid="ai-coach-athlete-trust-strip"
        >
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-4 w-4 text-[#004C54]" strokeWidth={2.5} />
            7-day free trial
          </span>
          <span className="text-[#004C54]/30 hidden sm:inline">&middot;</span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-4 w-4 text-[#004C54]" strokeWidth={2.5} />
            No credit card required
          </span>
          <span className="text-[#004C54]/30 hidden sm:inline">&middot;</span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-4 w-4 text-[#004C54]" strokeWidth={2.5} />
            Cancel anytime
          </span>
        </motion.div>

        {/* 2. How it Works — 3 numbered steps */}
        <div className="mb-16">
          <motion.div {...fadeIn} className="text-center mb-8">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54]/70 font-bold mb-2">
              How it Works
            </div>
            <h3 className="font-heading text-[1.5rem] sm:text-[1.75rem] font-bold tracking-tight text-[#0a0d0e]">
              Three steps to your first AI Coach rep.
            </h3>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
            {steps.map((s, i) => {
              const Icon = s.Icon;
              return (
                <motion.div
                  key={s.n}
                  {...fadeIn}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  data-testid={`ai-coach-athlete-step-${s.n}`}
                >
                  <Card className="h-full border border-[#004C54]/12 hover:border-[#004C54]/35 hover:shadow-md transition-all duration-200 relative overflow-hidden">
                    <CardContent className="p-6 sm:p-7">
                      <div className="flex items-center justify-between mb-4">
                        <span className="font-mono-accent text-[28px] sm:text-[32px] font-bold text-[#004C54]/15 leading-none tracking-tight">
                          {s.n}
                        </span>
                        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54]">
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <h4 className="font-heading text-[17px] sm:text-[18px] font-bold text-[#0a0d0e] mb-2 leading-tight">
                        {s.title}
                      </h4>
                      <p className="text-[14px] text-[#3a3a3a] leading-[1.55]">
                        {s.blurb}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* 3. Feature highlights — 5 tiles */}
        <div className="mb-14">
          <motion.div {...fadeIn} className="text-center mb-8">
            <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54]/70 font-bold mb-2">
              What You Get
            </div>
            <h3 className="font-heading text-[1.5rem] sm:text-[1.75rem] font-bold tracking-tight text-[#0a0d0e]">
              Five tools. One playbook. Always in your pocket.
            </h3>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {features.map((f, i) => {
              const Icon = f.Icon;
              return (
                <motion.div
                  key={f.name}
                  {...fadeIn}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-2xl border border-[#004C54]/10 bg-[#F6F7F8] p-5 hover:border-[#004C54]/25 hover:bg-white transition-colors"
                  data-testid={`ai-coach-athlete-feature-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#004C54] flex items-center justify-center mb-3">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-heading text-[14.5px] font-bold text-[#0a0d0e] mb-1.5 leading-tight">
                    {f.name}
                  </h4>
                  <p className="text-[12.5px] text-[#3a3a3a] leading-[1.5]">
                    {f.blurb}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* 4. Access clarification — two inclusion paths */}
        <motion.div
          {...fadeIn}
          transition={{ delay: 0.15 }}
          className="rounded-2xl bg-[#0B0C0D] text-white p-6 sm:p-8 mb-10"
          data-testid="ai-coach-athlete-access-panel"
        >
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#4FBCC7] font-bold mb-2">
                In a Coaching Package?
              </div>
              <p className="text-[14.5px] text-white/75 leading-[1.55]">
                You&rsquo;re already in. <strong className="text-white">Apex AI Coach access is included with all 3- and 6-session athlete packages</strong> &mdash; between sessions, no extra purchase needed.
              </p>
            </div>
            <div className="sm:border-l sm:border-white/10 sm:pl-8">
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#4FBCC7] font-bold mb-2">
                Just Want the App?
              </div>
              <p className="text-[14.5px] text-white/75 leading-[1.55]">
                Apex AI Coach is also available as a <strong className="text-white">standalone subscription</strong> &mdash; starting with a 7-day free trial, no credit card required.
              </p>
            </div>
          </div>
        </motion.div>

        {/* 5. CTAs — primary + secondary */}
        <motion.div
          {...fadeIn}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8"
        >
          <Button
            asChild
            className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold w-full sm:w-auto h-12 px-7 text-[15px] rounded-xl shadow-xl shadow-[#004C54]/20 active:scale-[0.98] transition-transform"
          >
            <a href="/app/signup" data-testid="ai-coach-athlete-primary-cta">
              <Sparkles className="mr-2 h-4 w-4" />
              Start Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="bg-transparent border-2 border-[#004C54]/30 hover:bg-[#004C54]/5 hover:border-[#004C54]/60 text-[#004C54] font-bold w-full sm:w-auto h-12 px-7 text-[15px] rounded-xl transition-colors"
          >
            <a
              href={CALENDLY_URL}
              data-testid="ai-coach-athlete-secondary-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a Discovery Call
            </a>
          </Button>
        </motion.div>

        {/* 6. Boundaries / scope note — kept verbatim from the original section */}
        <motion.div
          {...fadeIn}
          transition={{ delay: 0.25 }}
          className="max-w-[760px] mx-auto rounded-2xl border border-amber-200 bg-amber-50/60 p-5 sm:p-6"
          data-testid="ai-coach-athlete-boundaries"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <ShieldAlert className="h-4.5 w-4.5 text-amber-700" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <div className="font-mono-accent uppercase tracking-[0.18em] text-[10px] text-amber-800 font-bold mb-1">
                What the AI Coach is &mdash; and what it isn&rsquo;t
              </div>
              <p className="text-[13.5px] text-amber-900 leading-relaxed">
                Apex AI Coach is a <strong>performance training tool</strong>. It is <strong>not</strong>
                {' '}therapy, medical advice, crisis support, or a replacement for a licensed mental
                health professional, athletic trainer, or physician. If you are in crisis or need
                clinical support, please contact a licensed professional or, in the U.S., call or
                text <strong>988</strong>.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AICoachAthleteSection;
